import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

export default function CallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const { handleCallback: exchangeCode, isAuthenticated } = useAuth();
  const processedRef = useRef(false);

  // Navigate when authentication is confirmed
  useEffect(() => {
    if (isAuthenticated && !isProcessing) {
      console.log('[Auth Callback] Authentication confirmed, navigating...');
      toast.success('Connexion réussie');
      navigate('/poweradmin', { replace: true });
    }
  }, [isAuthenticated, isProcessing, navigate]);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (processedRef.current) return;
    processedRef.current = true;
    processCallback();
  }, []);

  const processCallback = async () => {
    try {
      // Check for errors in query params
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        console.error('[Auth Callback] Error:', errorParam, errorDescription);
        setError(errorDescription || errorParam);
        setIsProcessing(false);
        return;
      }

      // Get authorization code from query params (PKCE flow)
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      console.log('[Auth Callback] Code:', code ? 'present' : 'missing');
      console.log('[Auth Callback] State:', state ? 'present' : 'missing');

      if (code) {
        // Verify state for CSRF protection
        const savedState = sessionStorage.getItem('auth_state');
        if (state && savedState && state !== savedState) {
          setError('État de session invalide. Veuillez réessayer.');
          setIsProcessing(false);
          return;
        }

        // Exchange code for tokens
        console.log('[Auth Callback] Exchanging code...');
        await exchangeCode(code);
        console.log('[Auth Callback] Exchange complete, waiting for auth state...');
        setIsProcessing(false);
        return;
      }

      // Check if already authenticated
      const existingToken = localStorage.getItem('authToken');
      if (existingToken) {
        setIsProcessing(false);
        navigate('/poweradmin', { replace: true });
        return;
      }

      // No code found
      setError('Aucun code d\'autorisation reçu.');
      setIsProcessing(false);

    } catch (err) {
      console.error('Callback error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'authentification');
      setIsProcessing(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erreur d'authentification</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="btn-primary"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="spinner spinner-lg mx-auto mb-4" />
        <p className="text-gray-600">Authentification en cours...</p>
      </div>
    </div>
  );
}
